import { useEffect } from 'react'

export const SITE_NAME = 'MatryxLab'
export const SITE_URL = 'https://www.matryxlabs.com'
export const DEFAULT_DESCRIPTION =
  'Premium custom-printed mouse mats in 6 sizes. Upload your own artwork or choose from 15 curated collections. Your desk. Your art.'
export const DEFAULT_IMAGE = `${SITE_URL}/og-default.jpg`

export interface SeoProps {
  title?: string
  description?: string
  image?: string | null
  path?: string
  type?: 'website' | 'product'
  noindex?: boolean
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(selector)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`)
  if (!el) {
    el = document.createElement('link')
    el.setAttribute('rel', rel)
    document.head.appendChild(el)
  }
  el.setAttribute('href', href)
}

/**
 * Keeps <title>, description, canonical and Open Graph / Twitter tags in sync
 * with the current page during client-side navigation. The server injects the
 * same tags into the initial HTML for crawlers and link previews.
 */
export function useSeo({ title, description, image, path, type = 'website', noindex = false }: SeoProps) {
  useEffect(() => {
    const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Custom & Designer Mouse Mats`
    const desc = description || DEFAULT_DESCRIPTION
    const url = `${SITE_URL}${path ?? window.location.pathname}`
    const img = image || DEFAULT_IMAGE

    document.title = fullTitle
    setMeta('meta[name="description"]', 'name', 'description', desc)
    setMeta('meta[name="robots"]', 'name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow')
    setLink('canonical', url)
    setMeta('meta[property="og:title"]', 'property', 'og:title', fullTitle)
    setMeta('meta[property="og:description"]', 'property', 'og:description', desc)
    setMeta('meta[property="og:url"]', 'property', 'og:url', url)
    setMeta('meta[property="og:image"]', 'property', 'og:image', img)
    setMeta('meta[property="og:type"]', 'property', 'og:type', type)
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', fullTitle)
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', desc)
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', img)
  }, [title, description, image, path, type, noindex])
}

/** Strip HTML tags and collapse whitespace for use in a meta description. */
export function toPlainText(html?: string | null, max = 160): string {
  if (!html) return ''
  const text = html.replace(/<[^>]*>/g, ' ').replace(/&amp;/g, '&').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim()
  return text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text
}
